import { useLayoutEffect, useRef } from 'react';

import ResizeObserver from 'resize-observer-polyfill';

/**
 * While `holding`, scrolls `scrollElement` by however far `marker` moves in its content, so it
 * stays put on screen. Holding the end of the feed while a new row opens makes the row push older
 * rows up rather than push what follows the feed, e.g. the composer, down. A marker below the
 * viewport is left alone: content growing out of sight moves nothing the reader is looking at.
 */
export function useHoldInPlace({
  holding,
  marker,
  scrollElement,
}: {
  holding: boolean;
  marker: HTMLElement | null;
  scrollElement: HTMLElement | null;
}): void {
  const holdingRef = useRef(holding);

  useLayoutEffect(() => {
    holdingRef.current = holding;
  }, [holding]);

  useLayoutEffect(() => {
    if (!marker || !scrollElement) return;

    const getOffset = (): number =>
      marker.getBoundingClientRect().top -
      scrollElement.getBoundingClientRect().top +
      scrollElement.scrollTop;
    let offset = getOffset();
    const observer = new ResizeObserver(() => {
      const next = getOffset();
      const wasOnScreen = offset <= scrollElement.scrollTop + scrollElement.clientHeight;
      if (holdingRef.current && wasOnScreen) scrollElement.scrollTop += next - offset;
      offset = next;
    });

    for (const child of Array.from(scrollElement.children)) observer.observe(child);
    return () => observer.disconnect();
  }, [marker, scrollElement]);
}
